import { useEffect } from "react";
import { ParanoidModeConfig } from "../../paranoid-mode/@types";
import useReclassifySample from "./useReclassifySample";

import { PARANOID_MODE_SOURCES } from "../../../components/ClassificationSource";
import { getClassificationByLabels } from "../../../lib/enums/ThreatStatusEnum";
import { enums } from "../../../shim/jsbridge";

const { paranoidModeComponents = {}, threat_status: threatStatus = {} } =
  enums ?? {};

type Props = {
  sha1?: string;
  classification?: string;
  classificationSource?: number;
  classificationReason?: string;
  paranoidModeConfig?: ParanoidModeConfig;
};

const isReclassifiableClassification = (classification?: string) => {
  if (!classification) return false;
  return getClassificationByLabels(classification) !== threatStatus.MALICIOUS;
};

const useAutoReclassify = ({
  sha1,
  classification,
  classificationSource,
  classificationReason,
  paranoidModeConfig
}: Props) => {
  const { reclassify } = useReclassifySample();

  const { level, included = [] } = paranoidModeConfig ?? {};
  const isParanoidModeActive = !!(level && included?.length);
  const isNetworkParanoidMode =
    !!level && included.includes(paranoidModeComponents.NETWORK_REFERENCES);

  const isNetworkReferenceReason =
    classificationReason === "NETWORK_REFERENCES";

  // Reclassify in two cases:
  // 1. Paranoid mode turned ON AND classification_source is NOT included in paranoid mode sources
  // 2. Paranoid mode turned OFF AND classification_source IS included in paranoid mode sources
  // This ensures classification stays up to date when paranoid mode changes
  const shouldReclassifyAny =
    classificationSource &&
    isParanoidModeActive !==
      PARANOID_MODE_SOURCES.includes(classificationSource);

  // Reclassify when paranoid mode is on and:
  // 1. Sample classification is not malicious, OR
  // 2. Classification reason is NETWORK_REFERENCES
  // Network reference classification can change when it's retrieved from 0407
  // This ensures that we keep it up to date if network reference paranoid mode is enabled
  const shouldReclassifyNetwork =
    isNetworkParanoidMode &&
    (isReclassifiableClassification(classification) ||
      isNetworkReferenceReason);

  const shouldReclassify = shouldReclassifyAny || shouldReclassifyNetwork;

  useEffect(() => {
    if (!sha1 || !shouldReclassify) return;
    reclassify(sha1);
  }, [shouldReclassify, reclassify, sha1]);
};

export default useAutoReclassify;
